import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Lock } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from 'components/ui/sheet';
import { portalDialogCloseClass, upsellClass, upsellIconClass } from 'components/portal/upsell';
import { useIsMobile } from 'hooks/use-media-query';
import { cn } from 'lib/utils';
import type { VocabWord } from 'modules/vocabulary';

interface WordsExportProps {
  /** The plan includes the download. */
  unlocked: boolean;
  busy: boolean;
  words: VocabWord[];
  onDownload: () => void;
  onUpgrade: () => void;
  /** Sizing of the trigger button (the phone page stretches it). */
  className?: string;
}

const triggerClass = 'inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-bold';

function PlanBadge() {
  const { t } = useTranslation();
  return <span className="rounded-md bg-white/20 px-1.5 py-0.5 text-[10.5px] font-extrabold tracking-[0.06em]">{t('vocab.plan_badge')}</span>;
}

/** The blurred file sample under a "unlocked with a plan" pill. */
function ExportSample({ words, dashed = false }: { words: VocabWord[]; dashed?: boolean }) {
  const { t } = useTranslation();
  const sample = words.slice(0, 5);
  return (
    <div aria-label={t('vocab.export_sample')} role="group" className={cn('relative overflow-hidden rounded-2xl border border-border', dashed && 'border-dashed')}>
      <div aria-hidden="true" className="pointer-events-none select-none p-3.5 font-mono text-xs leading-[1.9] text-foreground/80 blur-[5px]">
        <div>{t('vocab.export_columns')}</div>
        {sample.map((word) => (
          <div key={word.id} className="truncate">{word.word}, …, "{word.context}"</div>
        ))}
      </div>
      <div className="absolute inset-0 grid place-items-center bg-gradient-to-b from-card/45 to-card/85">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-[12.5px] font-bold text-foreground">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          {t('vocab.export_locked')}
        </span>
      </div>
    </div>
  );
}

/**
 * "Faylga eksport". With a plan it downloads at once; without one the button opens a popover (a bottom sheet on
 * phones) holding a blurred sample of the file and the plan button, so the learner sees what they would get.
 */
export function WordsExport({ unlocked, busy, words, onDownload, onUpgrade, className }: WordsExportProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (unlocked) {
    return (
      <Button type="button" onClick={onDownload} disabled={busy} className={cn('gap-2', className)}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
        {t('vocab.export')}
      </Button>
    );
  }

  const header = (
    <div className="flex items-center gap-3.5 pr-6">
      <span className={cn(upsellIconClass, 'h-10 w-10 rounded-xl')}>
        <Download className="h-5 w-5" aria-hidden />
      </span>
      <div>
        <p className="font-display text-[17px] font-extrabold tracking-[-0.02em] text-foreground">{t('vocab.export_title')}</p>
        <p className="mt-0.5 text-[13px] text-muted-foreground">{t('vocab.export_body', { count: words.length })}</p>
      </div>
    </div>
  );
  const cta = (
    <>
      <button
        type="button"
        onClick={() => {
          setSheetOpen(false);
          onUpgrade();
        }}
        className={cn('mt-4 inline-flex h-12 w-full items-center justify-center rounded-xl text-sm font-bold', upsellClass)}
      >
        {t('vocab.export_cta')}
      </button>
      <p className="mt-2.5 text-center text-xs text-muted-foreground">{t('vocab.export_note')}</p>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <button type="button" aria-haspopup="dialog" className={cn(triggerClass, 'h-11', upsellClass, className)}>
            {t('vocab.export')}
            <PlanBadge />
          </button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          closeLabel={t('close')}
          closeClassName={portalDialogCloseClass}
          className="max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-t-3xl px-[18px] pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-20px_50px_-20px_rgba(16,24,43,0.5)]"
        >
          <div aria-hidden="true" className="mx-auto mb-3.5 h-1 w-10 rounded-full bg-border" />
          {/* The visible header below carries the same words; Radix needs a title and description to label the dialog. */}
          <SheetTitle className="sr-only">{t('vocab.export')}</SheetTitle>
          <SheetDescription className="sr-only">{t('vocab.export_body', { count: words.length })}</SheetDescription>
          {header}
          <div className="mt-4">
            <ExportSample words={words} dashed />
          </div>
          {cta}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-haspopup="dialog" className={cn(triggerClass, upsellClass, className)}>
          {t('vocab.export')}
          <PlanBadge />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(460px,calc(100vw-2rem))] rounded-3xl p-5 md:p-[22px]">
        {header}
        <div className="mt-3.5">
          <ExportSample words={words} />
        </div>
        {cta}
      </PopoverContent>
    </Popover>
  );
}

export default WordsExport;
